import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { api, type UserRef } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatDateTime, formatNumber, relativeTime } from '../lib/format';
import { Button, Card, CardHeader, Input, ProgressBar, useToast } from './ui';

/**
 * A task's to-do list (operator, 2026-10-09: "let me add to list with checkbox
 * for done also i can add and delete from list in edit task mode anytime task
 * creater and assign employees and leader can change the to do list").
 *
 * The same list on the task's page and on its edit page. Every change — a new
 * line, a tick, new words, a line taken off — is saved the moment it is made,
 * so it never waits for the edit form's Save. Whoever created the task, whoever
 * has it, team leaders and admins change it, whatever the task's status
 * (`can.checklist`, the API's own rule); anyone else who sees the task reads it.
 */

/** A line of the list, as `GET /tasks/:id` hands it out. */
export interface ChecklistItem {
  id: string;
  title: string;
  isDone: boolean;
  doneAt: string | null;
  doneBy: UserRef | null;
  createdBy: UserRef;
  createdAt: string;
}

/** What the list needs of its task: an API from before the list sends neither field, and it stays hidden. */
export interface ChecklistTask {
  id: string;
  checklist?: ChecklistItem[];
  can?: { checklist?: boolean };
}

/** How far a task's list has got, as the task lists and the board hand it out. */
export interface ChecklistProgress {
  done: number;
  total: number;
}

/** The API's limits: words per line, lines per list. */
const TITLE_MAX = 300;
const ITEMS_MAX = 100;

/** The task page's card. Someone who only reads the task gets no card for an empty list. */
export function TaskChecklistCard({ task }: { task: ChecklistTask }) {
  const { t } = useI18n();
  const items = task.checklist ?? [];
  if (!task.can?.checklist && items.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title={t('tasks.checklist.title')}
        subtitle={items.length > 0 ? <ProgressLine items={items} /> : undefined}
      />
      <TaskChecklist task={task} />
    </Card>
  );
}

/**
 * The edit page's section: the same list, below the fields. Its changes are
 * saved as they are made, which the hint says, since the page's Save is the
 * fields'.
 */
export function TaskChecklistSection({ task }: { task: ChecklistTask }) {
  const { t } = useI18n();
  const items = task.checklist ?? [];
  if (!task.can?.checklist && items.length === 0) return null;
  return (
    <section className="border-t hairline pt-5" aria-labelledby="task-checklist-heading">
      <h2 id="task-checklist-heading" className="text-h3 text-[var(--text-primary)]">
        {t('tasks.checklist.title')}
      </h2>
      {items.length > 0 && (
        <p className="mt-0.5 text-body-sm text-muted">
          <ProgressLine items={items} />
        </p>
      )}
      {task.can?.checklist && <p className="mt-1 text-caption text-faint">{t('tasks.checklist.savedAsYouGo')}</p>}
      <div className="mt-4">
        <TaskChecklist task={task} />
      </div>
    </section>
  );
}

/** "2 of 5 done" — nothing for an empty list. */
function ProgressLine({ items }: { items: ChecklistItem[] }) {
  const { t, locale } = useI18n();
  if (items.length === 0) return null;
  const done = items.filter((i) => i.isDone).length;
  return <>{t('tasks.checklist.progress', { done: formatNumber(done, locale), total: formatNumber(items.length, locale) })}</>;
}

/**
 * "2/5" beside a task on the board and in the task lists — nothing when its
 * list is empty. Green once every line is ticked.
 */
export function ChecklistProgressChip({
  progress,
  className,
}: {
  progress?: ChecklistProgress | null;
  className?: string;
}) {
  const { t, locale } = useI18n();
  if (!progress || progress.total === 0) return null;
  const done = formatNumber(progress.done, locale);
  const total = formatNumber(progress.total, locale);
  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center gap-1 text-caption tabular',
        progress.done === progress.total ? 'text-success-fg dark:text-success' : 'text-muted',
        className,
      )}
      title={t('tasks.checklist.chipTitle', { done, total })}
    >
      <ListChecks className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">{t('tasks.checklist.chipTitle', { done, total })}</span>
      <span aria-hidden>
        {done}/{total}
      </span>
    </span>
  );
}

/** Enter, but not the one that ends an input method's composition (Arabic keyboards use one). */
function isEnter(e: KeyboardEvent) {
  return e.key === 'Enter' && !e.nativeEvent.isComposing;
}

/** The list itself: its lines, each ticked, reworded or taken off in place, and a field to add one. */
function TaskChecklist({ task }: { task: ChecklistTask }) {
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);
  const items = task.checklist ?? [];
  const editable = !!task.can?.checklist;
  const full = items.length >= ITEMS_MAX;
  const base = `/tasks/${task.id}/checklist`;
  const key = ['task', task.id];

  // The task (its list and its history), and the lists and board that show how far it has got.
  const refresh = () => {
    for (const queryKey of [key, ['tasks'], ['kanban'], ['leader-tasks']]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };

  const add = useMutation({
    mutationFn: (title: string) => api.post<ChecklistItem>(base, { title }),
    onSuccess: () => {
      setDraft('');
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Ticked on screen at once; put back if the API refuses.
  const tick = useMutation({
    mutationFn: ({ item, isDone }: { item: ChecklistItem; isDone: boolean }) =>
      api.patch<ChecklistItem>(`${base}/${item.id}`, { isDone }),
    onMutate: async ({ item, isDone }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old: any) =>
        old?.checklist
          ? {
              ...old,
              checklist: old.checklist.map((line: ChecklistItem) =>
                line.id === item.id
                  ? {
                      ...line,
                      isDone,
                      doneAt: isDone ? new Date().toISOString() : null,
                      doneBy: isDone && user ? { id: user.id, fullName: user.fullName, fullNameAr: user.fullNameAr } : null,
                    }
                  : line,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (e: Error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      toast.error(e.message);
    },
    onSettled: refresh,
  });

  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => api.patch<ChecklistItem>(`${base}/${id}`, { title }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (item: ChecklistItem) => api.del(`${base}/${item.id}`),
    onSuccess: () => {
      toast.success(t('tasks.checklist.removed'));
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submitDraft = () => {
    const title = draft.trim();
    if (title && !add.isPending && !full) add.mutate(title);
  };
  const submitEdit = () => {
    if (!editing) return;
    const title = editing.title.trim();
    const before = items.find((i) => i.id === editing.id)?.title;
    if (!title) return;
    if (title === before) setEditing(null);
    else rename.mutate({ id: editing.id, title });
  };
  const done = items.filter((i) => i.isDone).length;

  return (
    <div>
      {items.length > 0 && (
        <div className="mb-3">
          <ProgressBar percent={(done / items.length) * 100} tone={done === items.length ? 'success' : 'brand'} />
        </div>
      )}

      {items.length === 0 ? (
        editable && <p className="mb-3 text-body-sm text-muted">{t('tasks.checklist.empty')}</p>
      ) : (
        <ul className="-mx-2 mb-1 space-y-0.5">
          {items.map((item) => {
            const busy =
              (tick.isPending && tick.variables?.item.id === item.id) ||
              (remove.isPending && remove.variables?.id === item.id);
            return (
              <li key={item.id} className="flex items-start gap-1 rounded-md px-2 py-1.5 hover:bg-[var(--surface-sunken)]">
                {editing?.id === item.id ? (
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <Input
                      value={editing.title}
                      onChange={(e) => setEditing({ id: item.id, title: e.target.value })}
                      onKeyDown={(e) => {
                        // Inside the edit form too: Enter saves the line, never the form.
                        if (isEnter(e)) {
                          e.preventDefault();
                          submitEdit();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditing(null);
                        }
                      }}
                      maxLength={TITLE_MAX}
                      aria-label={t('tasks.checklist.editLine')}
                      className="h-8 min-w-0 flex-1 basis-48"
                      dir="auto"
                      autoFocus
                    />
                    <span className="flex shrink-0 gap-1.5">
                      <Button
                        size="sm"
                        variant="primary"
                        loading={rename.isPending}
                        disabled={!editing.title.trim()}
                        onClick={submitEdit}
                      >
                        {t('common.save')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        {t('common.cancel')}
                      </Button>
                    </span>
                  </div>
                ) : (
                  <>
                    <label
                      className={clsx(
                        'flex min-w-0 flex-1 items-start gap-2.5',
                        editable ? 'cursor-pointer' : 'cursor-default',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={item.isDone}
                        disabled={!editable || busy}
                        onChange={(e) => tick.mutate({ item, isDone: e.target.checked })}
                        aria-label={item.title}
                        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary disabled:cursor-default"
                      />
                      <span className="min-w-0">
                        <span
                          className={clsx(
                            'block whitespace-pre-wrap break-words text-body-sm',
                            item.isDone ? 'text-muted line-through' : 'text-[var(--text-primary)]',
                          )}
                          dir="auto"
                        >
                          {item.title}
                        </span>
                        {item.isDone && (
                          <span className="block text-caption text-faint" title={formatDateTime(item.doneAt, locale)}>
                            {item.doneBy
                              ? t('tasks.checklist.doneBy', {
                                  name: localized(item.doneBy),
                                  when: relativeTime(item.doneAt, locale),
                                })
                              : relativeTime(item.doneAt, locale)}
                          </span>
                        )}
                      </span>
                    </label>
                    {editable && (
                      <span className="flex shrink-0 items-center">
                        <LineButton label={t('tasks.checklist.editLine')} onClick={() => setEditing({ id: item.id, title: item.title })}>
                          <Pencil className="h-3.5 w-3.5" aria-hidden />
                        </LineButton>
                        <LineButton label={t('tasks.checklist.removeLine')} onClick={() => remove.mutate(item)} disabled={busy}>
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        </LineButton>
                      </span>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editable && (
        <div className="mt-2">
          <div className="flex items-center gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter adds the line and keeps the field, for the next one — never submits the edit form around it.
                if (isEnter(e)) {
                  e.preventDefault();
                  submitDraft();
                }
              }}
              placeholder={t('tasks.checklist.addPlaceholder')}
              aria-label={t('tasks.checklist.addPlaceholder')}
              maxLength={TITLE_MAX}
              disabled={full}
              dir="auto"
              className="min-w-0 flex-1"
            />
            <Button
              icon={<Plus className="h-4 w-4" />}
              loading={add.isPending}
              disabled={!draft.trim() || full}
              onClick={submitDraft}
            >
              {t('common.add')}
            </Button>
          </div>
          {full && (
            <p className="mt-1 text-caption text-faint">
              {t('tasks.checklist.full', { n: formatNumber(ITEMS_MAX, locale) })}
            </p>
          )}
        </div>
      )}
      {/* Read only: whose list it is to change, rather than checkboxes that silently do nothing. */}
      {!editable && items.length > 0 && <p className="mt-2 text-caption text-faint">{t('tasks.checklist.whoChanges')}</p>}
    </div>
  );
}

/** A line's own small button: reword it, take it off. */
function LineButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="grid h-7 w-7 place-items-center rounded-full text-faint transition-colors duration-fast hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] disabled:opacity-50"
    >
      {children}
    </button>
  );
}
